import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useData, DataProvider } from './DataContext';
import { Layout, Menu, Button, theme, ConfigProvider, Dropdown } from 'antd'; 
import { 
  MenuUnfoldOutlined, MenuFoldOutlined, 
  UserOutlined, BankOutlined, 
  UnorderedListOutlined, TransactionOutlined, 
  LogoutOutlined, DashboardOutlined, 
  BarChartOutlined, DollarOutlined, SettingOutlined,
  GlobalOutlined 
} from '@ant-design/icons';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import GameList from './pages/GameList';
import Agents from './pages/Agents'; 
import CreditPage from './pages/CreditPage';
import TransactionHistory from './pages/TransactionHistory'; 
import Reports from './pages/Reports';
import Cage from './pages/Cage';
import Settings from './pages/Settings';
import Settlement from './pages/Settlement';

import './App.css'; 
import { useLang, LanguageProvider } from './LanguageContext';

const { Header, Sider, Content } = Layout;

const AppContent = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { currentUser, logout } = useData();
  const { t, lang, setLang } = useLang(); 
  const location = useLocation();
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

  // 로그인 상태가 아니면 로그인 페이지로
  if (!currentUser) {
    return <Login />;
  }

  // 관리자 권한 목록
  const adminRoles = ['Master_Admin', 'Senior Manager', 'Manager', 'Staff'];
  const isAdmin = adminRoles.includes(currentUser.type);

  const handleLogout = () => {
    if (window.confirm(t('confirm') + '? (Logout)')) {
      logout();
    }
  };

  // 언어 선택 메뉴 설정
  const langMenu = {
    items: [
      { key: 'KR', label: '한국어 (Korean)', onClick: () => setLang('KR') },
      { key: 'EN', label: 'English', onClick: () => setLang('EN') },
      { key: 'CN', label: '中文 (Chinese)', onClick: () => setLang('CN') },
      { key: 'JP', label: '日本語 (Japanese)', onClick: () => setLang('JP') },
    ]
  };

  // 기본 메뉴 (모든 사용자에게 보임)
  const baseMenuItems = [
    { key: '/', icon: <DashboardOutlined />, label: <Link to="/">{t('menu_dashboard')}</Link> },
    { type: 'divider' }, 
    { key: '/agents', icon: <UserOutlined />, label: <Link to="/agents">{t('menu_agents')}</Link> },
    { type: 'divider' }, 
    { key: '/games', icon: <UnorderedListOutlined />, label: <Link to="/games">{t('menu_games')}</Link> },
    { key: '/reports', icon: <BarChartOutlined />, label: <Link to="/reports">{t('menu_reports')}</Link> },
    { key: '/transactions', icon: <TransactionOutlined />, label: <Link to="/transactions">{t('menu_transactions')}</Link> },
  ];

  // 관리자 전용 메뉴
  const adminMenuItems = [
    { key: '/credit', icon: <DollarOutlined />, label: <Link to="/credit">{t('menu_credit')}</Link> },
    { type: 'divider' }, 
    { key: '/cage', icon: <BankOutlined />, label: <Link to="/cage">{t('menu_cage')}</Link> },
    { type: 'divider' }, 
    { key: '/settings', icon: <SettingOutlined />, label: <Link to="/settings">{t('menu_settings')}</Link> },
  ];

  // isAdmin 값에 따라 메뉴 병합
  const menuItems = isAdmin ? [...baseMenuItems, ...adminMenuItems] : baseMenuItems;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* ★ [모바일 호환] breakpoint="lg" 설정으로 화면 작아지면 자동 접힘 */}
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed} 
        breakpoint="lg"
        collapsedWidth="0"
        onBreakpoint={(broken) => {
            if(broken) setCollapsed(true);
        }}
        theme="dark" 
        width={250}
        style={{
            position: 'sticky',
            top: 0,
            left: 0,
            height: '100vh',
            zIndex: 1000 // 다른 요소보다 위에 오도록
        }}
      >
        <div className="demo-logo-vertical" style={{ 
            height: '64px', margin: '16px', background: 'rgba(255, 255, 255, 0.1)', 
            borderRadius: '6px', display:'flex', alignItems:'center', justifyContent:'center',
            color:'white', fontWeight:'bold', fontSize:'18px', letterSpacing:'1px'
        }}>
          {collapsed ? '' : 'JUNKET SYS'}
        </div>
        
        <Menu theme="dark" mode="inline" selectedKeys={[location.pathname]} items={menuItems} style={{ fontSize: '15px', fontWeight: '500' }} />
        
        <div style={{ position: 'absolute', bottom: '20px', width: '100%', padding: '0 24px' }}>
            {!collapsed && (
                <Button type="primary" danger icon={<LogoutOutlined />} onClick={handleLogout} block style={{ height:'45px', fontWeight:'bold' }}>Logout</Button>
            )}
        </div>
      </Sider>
      
      <Layout>
        <Header style={{ padding: 0, background: colorBgContainer, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '20px', position:'sticky', top:0, zIndex:999, boxShadow: '0 2px 8px #f0f1f2' }}>
          
          <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed(!collapsed)} style={{ fontSize: '16px', width: 64, height: 64 }} />
          
          <div style={{ display:'flex', alignItems:'center', gap:'15px' }}>
             {/* 언어 변경 버튼 */}
             <Dropdown menu={langMenu} placement="bottomRight">
                <Button icon={<GlobalOutlined />}>{lang}</Button>
             </Dropdown>

             {/* ★ [수정] 프로필 정보 잘림 방지 (세로 정렬 적용) */}
             <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', justifyContent:'center', lineHeight:'1.2', marginRight:'5px' }}>
                 <span style={{ fontWeight:'bold', fontSize:'14px', color:'#1e293b' }}>
                    {currentUser.engSurname}
                 </span>
                 <span style={{ fontSize:'11px', color:'#64748b' }}>
                    {currentUser.type}
                 </span>
             </div>
          </div>
        </Header>
        
        <Content style={{ margin: '16px', padding: 20, minHeight: 280, background: '#f1f5f9', borderRadius: borderRadiusLG, overflowY: 'auto' }}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/games" element={<GameList />} />
            <Route path="/agents" element={<Agents />} />
            
            {/* 권한 보호 라우트: isAdmin이 false면 대시보드로 리다이렉트 */}
            <Route path="/credit" element={isAdmin ? <CreditPage /> : <Navigate to="/" replace />} />
            <Route path="/transactions" element={<TransactionHistory />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/cage" element={isAdmin ? <Cage /> : <Navigate to="/" replace />} />
            <Route path="/settings" element={isAdmin ? <Settings /> : <Navigate to="/" replace />} />
            
            <Route path="/settlement" element={<Settlement />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
};

const App = () => {
  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#2563eb' }, components: { Menu: { darkItemColor: '#a6adb4', darkItemBg: '#001529', darkPopupBg: '#001529' } } }}>
        <DataProvider>
            <LanguageProvider>
                <Router>
                    <AppContent />
                </Router>
            </LanguageProvider>
        </DataProvider>
    </ConfigProvider>
  );
};

export default App;